import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, CircleAlert, FilePlus, Files, Users } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { OrganizationCombobox } from "@/components/organizations-selected/organization-selected";
import { toast } from "react-toastify";
import { createVisit, validateVisitData } from "@/lib/collections/visits/user/addVisitFetch";
import { fetchUsersWithProfiles } from "@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile"
import { Switch } from "@/components/ui/switch";
import MultipleSelector from "@/components/ui/multiple-selector";


const AddVisit = () => {
    const { userRoles, user } = useAuth()
    const userId = useAuth().user?.$id;
    const navigate = useNavigate();

    const [visitPurpose, setVisitPurpose] = useState<string>('');
    const [branch, setBranch] = useState<string>('');
    const [openStartDate, setOpenStartDate] = useState(false);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [startTime, setStartTime] = useState<string>('09:00:00');
    const [endTime, setEndTime] = useState<string>('11:00:00');
    const [sameMotif, setSameMotif] = useState(false);

    const [organizationAddress, setOrganizationAddress] = useState('');
    const [organizationCity, setOrganizationCity] = useState('');
    const [organizationId, setOrganizationId] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [timeError, setTimeError] = useState<string>('');

    //para añadir mas de 1 colaborador
    const [isGroup, setIsGroup] = useState(false);
    const [collaborators, setCollaborators] = useState<{ label: string; value: string; email?: string }[]>([]);
    const [availableCollaborators, setAvailableCollaborators] = useState<{ label: string; value: string; email?: string }[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersLoadError, setUsersLoadError] = useState<string | null>(null)
    const handleIsGroupChange = (checked: boolean) => {
        setIsGroup(checked);
        if (!checked) setSameMotif(false);
    };
    useEffect(() => {
        if (!isGroup) return;
        if (!userId) return; // esperar a que userId esté disponible

        const load = async () => {
            try {
                setLoadingUsers(true);
                setUsersLoadError(null);
                const users = await fetchUsersWithProfiles();

                const collaboratorOptions = users
                    .filter(user => user.id !== userId && user.profile?.isActive !== false)
                    .map(user => ({ label: user.name, value: user.id, email: user.email }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setAvailableCollaborators(collaboratorOptions);
            } catch (e: any) {
                setUsersLoadError(e.message || 'Error al cargar colaboradores');
            } finally {
                setLoadingUsers(false);
            }
        };
        load();
    }, [isGroup, userId]); // añadir userId como dependencia


    const handleSubmit = async () => {
        // Validaciones
        const userEmail = user?.email || '';

        const validationError = validateVisitData({
            userId: userId || '',
            organizationId,
            visitPurpose: visitPurpose.trim(),
            startDate: startDate!,
            startTime,
            endTime,
            branch,
            userEmail

        });

        if (validationError) {
            toast.warning(validationError);
            return;
        }

        if (!userId) {
            toast.warning('Usuario no identificado');
            return;
        }

        if (!startDate) {
            toast.warning('Debe seleccionar una fecha');
            return;
        }

        if (timeError) {
            toast.warning('Corrija los errores en el formulario antes de continuar');
            return;
        }


        setIsSubmitting(true);

        try {
            const result = await createVisit({
                userId,
                collaboratorIds: isGroup ? collaborators.map(collaborator => collaborator.value) : [], // crear mas de 1 registro en la bdd segun los colaboradores añadidos
                collaboratorsEmails: isGroup ? collaborators.map(collaborator => collaborator.email || '').filter(Boolean) : [],

                organizationId,
                visitPurpose,
                startDate,
                startTime,
                endTime,
                branch,
                userEmail,
                sameMotif
            });

            if (result.success) {
                toast.success('Visita registrada correctamente');

                // Limpia el formulario
                setOrganizationId('');
                setOrganizationName('');
                setVisitPurpose('');
                setStartDate(undefined);
                setStartTime('10:00:00');
                setEndTime('14:00:00');
                setBranch('');
            } else {
                toast.error(result.error || 'Error al guardar la visita');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error inesperado al guardar');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Solicitudes</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests/$id/visit" params={{ id: userId! }}>Registros</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />

                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Agregar registro</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden">
                            <FilePlus className="w-5 h-5" />
                        </BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>




            <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 font-outfit">
                <div className="max-w-8xl mx-auto">
                    <Card className="shadow-xl border-0 p-0">
                        <CardHeader className="relative text-white rounded-t-lg bg-blue-500 min-h-20 sm:h-20">
                            <div className="absolute top-0 rounded-t-4xl left-0 w-full h-full bg-blue-500"></div>
                            <div className="relative z-10 flex items-center gap-4 py-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <FilePlus className="w-6 h-6" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <CardTitle className="text-2xl">Registrar Visita</CardTitle>
                                    <CardDescription className="text-blue-100">
                                        Completa el formulario para registrar tu visita
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>

                        <CardContent className="p-6 sm:p-8">
                            <div className="space-y-8">
                                {/*  informacion de la Visita */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            1
                                        </div>
                                        Información de la Visita
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="companyName" className="text-sm font-medium mb-2">Nombre de empresa / laboratorio:</Label>
                                            <OrganizationCombobox
                                                value={organizationId}
                                                onChange={(id, name, address, city) => {
                                                    setOrganizationId(id);
                                                    setOrganizationName(name);
                                                    setOrganizationAddress(address || '');
                                                    setOrganizationCity(city || '');

                                                }}
                                            />
                                        </div>

                                        {/*  informnacion de la empresa seleccionada */}
                                        {organizationId && (organizationAddress || organizationCity) && (
                                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
                                                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                                                    Información de la empresa
                                                </h4>

                                                {organizationName && (
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-sm font-medium text-blue-500 dark:text-blue-300">
                                                            Nombre:
                                                        </span>
                                                        <span className="text-sm text-blue-900 dark:text-blue-100">
                                                            {organizationName}
                                                        </span>
                                                    </div>
                                                )}

                                                {organizationCity && (
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-sm font-medium text-blue-500 dark:text-blue-300">
                                                            Ciudad:
                                                        </span>
                                                        <span className="text-sm text-blue-900 dark:text-blue-100">
                                                            {organizationCity}
                                                        </span>
                                                    </div>
                                                )}

                                                {organizationAddress && (
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-sm font-medium text-blue-500 dark:text-blue-300 ">
                                                            Dirección:
                                                        </span>
                                                        <span className="text-sm text-blue-900 dark:text-blue-100">
                                                            {organizationAddress}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div>
                                            <Label htmlFor="companyName" className="text-sm font-medium mb-2">Sucursal: <span className="text-xs text-gray-500 dark:text-gray-400">Opcional*: (Ingresa la sucursal únicamente si la visita no es a la matriz de la empresa. )</span></Label>
                                            <Input
                                                id="branch"
                                                name="branch"
                                                value={branch}
                                                onChange={event => setBranch(event.target.value)}
                                                placeholder="Opcional..." />
                                        </div>


                                        <div>
                                            <Label htmlFor="visitPurpose" className="text-sm font-medium">Objetivo de visita:</Label>
                                            <Textarea
                                                id="visitPurpose"
                                                name="visitPurpose"
                                                value={visitPurpose}
                                                onChange={(e) => setVisitPurpose(e.target.value)}
                                                placeholder="Ingrese en el campo..."
                                                className="mt-1.5" />
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            2
                                        </div>
                                        Colaboradores
                                    </h3>

                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                            <Switch
                                                id="isGroup"
                                                checked={isGroup}
                                                onCheckedChange={handleIsGroupChange}
                                            />
                                            <Label htmlFor="isGroup" className="cursor-pointer flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                ¿Visita con más colaboradores?
                                            </Label>
                                        </div>

                                        {isGroup && (
                                            <div className="space-y-3">
                                                {usersLoadError && (
                                                    <p className="text-sm text-amber-600">{usersLoadError}</p>
                                                )}
                                                <Label className="text-sm font-medium">Selecciona colaboradores</Label>
                                                <MultipleSelector
                                                    value={collaborators}
                                                    onChange={setCollaborators}
                                                    defaultOptions={availableCollaborators}
                                                    placeholder={loadingUsers ? "Cargando..." : "Buscar colaboradores..."}
                                                    disabled={loadingUsers || !!usersLoadError}
                                                    emptyIndicator={<p className="text-center text-sm py-4 text-slate-500">No se encontraron colaboradores</p>}
                                                />
                                            </div>
                                        )}

                                        {isGroup && collaborators.length > 0 && (
                                            <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <Switch
                                                    id="sameMotif"
                                                    checked={sameMotif}
                                                    onCheckedChange={setSameMotif}
                                                />
                                                <Label htmlFor="sameMotif" className="cursor-pointer flex items-center gap-2">
                                                    <Files className="w-4 h-4" />
                                                    ¿Todos van por el mismo motivo?
                                                </Label>
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {/*  Fechas y Horarios */}
                                <section>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 text-sm font-bold">
                                            3
                                        </div>
                                        Fechas y Horarios
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div>
                                                <Label className="text-sm font-medium mb-2 block">Fecha de visita </Label>
                                                <Popover open={openStartDate} onOpenChange={setOpenStartDate}>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            className="w-full justify-between font-normal"
                                                        >
                                                            {startDate ? startDate.toLocaleDateString('es-ES', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })
                                                                : "Seleccionar fecha"}
                                                            <ChevronDown className="w-4 h-4 opacity-50" />
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start">
                                                        <Calendar
                                                            mode="single"
                                                            selected={startDate}
                                                            disabled={{ before: new Date() }}
                                                            captionLayout="dropdown"
                                                            onSelect={(date) => {
                                                                setStartDate(date);
                                                                setOpenStartDate(false);
                                                            }} />
                                                    </PopoverContent>
                                                </Popover>
                                            </div>

                                        </div>

                                        <div>
                                            <Label htmlFor="time-picker-start" className="text-sm font-medium">Hora Inicial <span className="text-xs text-gray-400">(aproximadamente)</span></Label>
                                            <Input
                                                type="time"
                                                id="time-picker-start"
                                                step="1"
                                                value={startTime}
                                                onChange={(e) => {
                                                    const newStartTime = e.target.value;
                                                    setStartTime(newStartTime);

                                                    // Validar con endTime
                                                    const [startHour, startMinute] = newStartTime.split(':').map(Number);
                                                    const [endHour, endMinute] = endTime.split(':').map(Number);

                                                    const startMinutes = startHour * 60 + startMinute;
                                                    const endMinutes = endHour * 60 + endMinute;

                                                    if (endMinutes <= startMinutes) {
                                                        setTimeError('La hora de salida debe ser posterior a la hora inicial');
                                                    } else {
                                                        setTimeError('');
                                                    }
                                                }}
                                                className="mt-1.5 w-full bg-background [&::-webkit-calendar-picker-indicator]:brightness-0 dark:[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                {/* Input hora finalizacion */}

                                                <Label htmlFor="time-picker-end" className="text-sm font-medium">Hora de Finalización<span className="text-xs text-gray-400">(aproximadamente)</span> </Label>
                                                <Input
                                                    type="time"
                                                    id="time-picker-end"
                                                    step="1"
                                                    value={endTime}
                                                    onChange={(e) => {
                                                        const newEndTime = e.target.value;
                                                        setEndTime(newEndTime);

                                                        // Validar con startTime
                                                        const [startHour, startMinute] = startTime.split(':').map(Number);
                                                        const [endHour, endMinute] = newEndTime.split(':').map(Number);

                                                        const startMinutes = startHour * 60 + startMinute;
                                                        const endMinutes = endHour * 60 + endMinute;

                                                        if (endMinutes <= startMinutes) {
                                                            setTimeError('La hora de salida debe ser posterior a la hora inicial');
                                                        } else {
                                                            setTimeError('');
                                                        }
                                                    }}
                                                    className={`mt-1.5 w-full bg-background ${timeError ? 'border-red-500' : ''}`} />

                                                {/* Mensaje de error */}
                                                {timeError && (
                                                    <p className="text-sm text-red-500 mt-1.5 flex items-center gap-1">
                                                        <span className="text-xs"> <CircleAlert />  </span>
                                                        {timeError}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            {/* Botones de Accion */}
                            <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t">
                                <Button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 cursor-pointer text-white font-medium"
                                >
                                    {isSubmitting ? 'Guardando...' : 'Registrar visita'}
                                </Button>

                                <Button
                                    variant="outline"
                                    className="flex-1 sm:flex-none sm:px-8 cursor-pointer"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (userId) {
                                            navigate({ to: `/requests/$id/visit`, params: { id: userId } });
                                        }
                                    }}
                                    hidden={!(userRoles?.isAsesorComercial || userRoles?.isServicioTecnico)}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Atrás
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default AddVisit;